import { type ClientSignalFlag, type PeakSeason } from '@/features/reports/api';
import { CLIENTS_COPY } from '@/features/reports/copy';
import { formatPounds } from '@/features/reports/format';
import { CLIENT_SIGNAL, INACTIVE_WINDOWS } from '@/features/reports/tabs';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "a-list-clients-spend-month-2026-10-23.xlsx", as the API names the same file. */
export function exportFileName(parts: (string | number)[]): string {
    const day = new Date().toISOString().slice(0, 10);
    return `${['a-list-clients', ...parts].map((p) => String(p).toLowerCase()).join('-')}-${day}.xlsx`;
}

/** 1 is "Jan". */
export const monthName = (month: number) => MONTHS[month - 1] ?? '';

/** "Mar, Nov". */
export const monthsText = (months: readonly number[]) => months.map(monthName).join(', ');

/** "Nov to Jan; May", or "All year" for a client who buys every month. */
export function seasonsText(seasons: readonly PeakSeason[]): string {
    return seasons
        .map(({ startMonth, endMonth }) =>
            startMonth === 1 && endMonth === 12
                ? CLIENTS_COPY.cycles.allYear
                : startMonth === endMonth
                  ? monthName(startMonth)
                  : `${monthName(startMonth)} to ${monthName(endMonth)}`,
        )
        .join('; ');
}

/** A signal's pill, in the prototype's words: "Wallet £250 credit". */
export function signalText({ kind, amount }: ClientSignalFlag): string {
    const S = CLIENTS_COPY.spend.signals;
    switch (kind) {
        case CLIENT_SIGNAL.AwaitingPayment:
            return S.AwaitingPayment(formatPounds(amount ?? 0));
        case CLIENT_SIGNAL.WalletCredit:
            return S.WalletCredit(formatPounds(amount ?? 0));
        default:
            return S[kind];
    }
}

/**
 * The windows to offer: 90, 180 and 365 days, and the configured default among them in order when
 * it is none of those (Reports:InactiveAfterDays), so the default is always one tap away.
 */
export function inactiveWindows(defaultDays: number): number[] {
    return [...new Set<number>([...INACTIVE_WINDOWS, defaultDays])].sort((a, b) => a - b);
}
